import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { format, subDays, eachDayOfInterval, parseISO, startOfWeek, endOfWeek, getDay, startOfMonth, endOfMonth } from 'date-fns';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiArrowLeft, FiBarChart3, FiTrendingUp, FiTrendingDown, FiCalendar, FiTarget, FiClock, FiSun, FiMoon, FiAward, FiUser } = FiIcons;

const ChildAnalytics = () => {
  const { childId } = useParams();
  const { children, sleep_logs, earned_rewards, rewards, getChildStats } = useApp();
  const [timeRange, setTimeRange] = useState('30'); // 7, 30, 90 days

  const child = children.find(c => c.id === parseInt(childId));
  const stats = getChildStats(parseInt(childId));

  const timeRangeOptions = [
    { value: '7', label: 'Last 7 Days' },
    { value: '30', label: 'Last 30 Days' },
    { value: '90', label: 'Last 90 Days' }
  ];

  // Calculate analytics data
  const analyticsData = useMemo(() => {
    const days = parseInt(timeRange);
    const endDate = new Date();
    const startDate = subDays(endDate, days - 1);
    const dateRange = eachDayOfInterval({ start: startDate, end: endDate });

    const childLogs = sleep_logs.filter(log => 
      log.childId === parseInt(childId) &&
      parseISO(log.date) >= startDate &&
      parseISO(log.date) <= endDate
    );

    // Basic stats
    const totalNights = childLogs.length;
    const goodNights = childLogs.filter(log => log.good).length;
    const successRate = totalNights > 0 ? Math.round((goodNights / totalNights) * 100) : 0;

    // Recent performance (last 2 weeks vs previous period)
    const recentLogs = childLogs.filter(log => 
      parseISO(log.date) >= subDays(endDate, 13)
    );
    const recentGoodNights = recentLogs.filter(log => log.good).length;
    const recentSuccessRate = recentLogs.length > 0 ? Math.round((recentGoodNights / recentLogs.length) * 100) : 0;

    // Weekly pattern analysis
    const weeklyData = [
      { day: 'Sunday', dayIndex: 0, logs: [], successRate: 0 },
      { day: 'Monday', dayIndex: 1, logs: [], successRate: 0 },
      { day: 'Tuesday', dayIndex: 2, logs: [], successRate: 0 },
      { day: 'Wednesday', dayIndex: 3, logs: [], successRate: 0 },
      { day: 'Thursday', dayIndex: 4, logs: [], successRate: 0 },
      { day: 'Friday', dayIndex: 5, logs: [], successRate: 0 },
      { day: 'Saturday', dayIndex: 6, logs: [], successRate: 0 }
    ];

    childLogs.forEach(log => {
      const dayOfWeek = getDay(parseISO(log.date));
      weeklyData[dayOfWeek].logs.push(log);
    });

    weeklyData.forEach(dayData => {
      const dayGoodNights = dayData.logs.filter(log => log.good).length;
      dayData.successRate = dayData.logs.length > 0 ? Math.round((dayGoodNights / dayData.logs.length) * 100) : 0;
      dayData.totalNights = dayData.logs.length;
      dayData.goodNights = dayGoodNights;
    });

    // Find best performing day
    const bestDay = weeklyData.reduce((best, current) => 
      current.successRate > best.successRate ? current : best
    , weeklyData[0]);

    // Monthly trend
    const currentMonth = format(endDate, 'MMMM yyyy');
    const monthStart = startOfMonth(endDate);
    const monthEnd = endOfMonth(endDate);
    const monthLogs = childLogs.filter(log => {
      const logDate = parseISO(log.date);
      return logDate >= monthStart && logDate <= monthEnd;
    });
    const monthGoodNights = monthLogs.filter(log => log.good).length;
    const monthSuccessRate = monthLogs.length > 0 ? Math.round((monthGoodNights / monthLogs.length) * 100) : 0;

    // Rewards in period
    const rewardsInPeriod = earned_rewards.filter(reward => {
      const earnedDate = parseISO(reward.earnedOn);
      return reward.childId === parseInt(childId) && 
             earnedDate >= startDate && 
             earnedDate <= endDate;
    });

    return {
      totalNights,
      goodNights,
      successRate,
      recentSuccessRate,
      weeklyData,
      bestDay,
      monthSuccessRate,
      monthGoodNights,
      monthTotalNights: monthLogs.length,
      currentMonth,
      rewardsEarned: rewardsInPeriod.length
    };
  }, [timeRange, childId, sleep_logs, earned_rewards]);

  const getSuccessRateColor = (rate) => {
    if (rate >= 80) return 'text-green-600 bg-green-50';
    if (rate >= 60) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  const getSuccessRateBarColor = (rate) => {
    if (rate >= 80) return 'bg-green-500';
    if (rate >= 60) return 'bg-yellow-500';
    return 'bg-red-500';
  };

  if (!child) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-700">Child not found</h2>
        <Link to="/" className="text-purple-600 hover:underline mt-2 inline-block">
          Return to Dashboard
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <Link
            to={`/child/${childId}`}
            className="p-2 hover:bg-white rounded-lg transition-colors"
          >
            <SafeIcon icon={FiArrowLeft} className="text-xl text-gray-600" />
          </Link>
          <div className="flex items-center space-x-3">
            <div className="text-3xl">{child.avatarEmoji}</div>
            <div>
              <h1 className="text-3xl font-bold text-gray-800">{child.name}'s Analytics</h1>
              <p className="text-gray-600">Sleep behavior patterns and insights</p>
            </div>
          </div>
        </div>

        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
        >
          {timeRangeOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl shadow-sm border border-green-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-green-200 rounded-lg">
              <SafeIcon icon={FiTrendingUp} className="text-green-600 text-xl" />
            </div>
            <span className="text-xs font-medium text-green-600 bg-green-200 px-2 py-1 rounded-full">
              {analyticsData.recentSuccessRate >= analyticsData.successRate ? 'Improving' : 'Declining'}
            </span>
          </div>
          <h3 className="text-sm font-medium text-green-700 mb-1">Recent Performance</h3>
          <p className="text-3xl font-bold text-green-800">{analyticsData.recentSuccessRate}%</p>
          <p className="text-xs text-green-600 mt-1">Last 2 weeks success rate</p>
        </div>

        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl shadow-sm border border-blue-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-blue-200 rounded-lg">
              <SafeIcon icon={FiCalendar} className="text-blue-600 text-xl" />
            </div>
            <span className="text-xs font-medium text-blue-600 bg-blue-200 px-2 py-1 rounded-full">
              {analyticsData.bestDay.day}
            </span>
          </div>
          <h3 className="text-sm font-medium text-blue-700 mb-1">Best Day</h3>
          <p className="text-3xl font-bold text-blue-800">{analyticsData.bestDay.day}</p>
          <p className="text-xs text-blue-600 mt-1">{analyticsData.bestDay.successRate}% success rate</p>
        </div>

        <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl shadow-sm border border-purple-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <div className="p-2 bg-purple-200 rounded-lg">
              <SafeIcon icon={FiTarget} className="text-purple-600 text-xl" />
            </div>
            <span className="text-xs font-medium text-purple-600 bg-purple-200 px-2 py-1 rounded-full">
              {analyticsData.totalNights} nights
            </span>
          </div>
          <h3 className="text-sm font-medium text-purple-700 mb-1">Total Nights</h3>
          <p className="text-3xl font-bold text-purple-800">{analyticsData.totalNights}</p>
          <p className="text-xs text-purple-600 mt-1">Nights tracked so far</p>
        </div>
      </div>

      {/* Weekly Pattern Analysis */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <SafeIcon icon={FiBarChart3} className="text-purple-600 text-xl" />
          <h3 className="text-lg font-semibold text-gray-800">Weekly Pattern Analysis</h3>
        </div>
        
        <div className="space-y-4">
          {analyticsData.weeklyData.map((dayData) => (
            <div key={dayData.day} className="flex items-center space-x-4">
              <div className="w-20 text-sm font-medium text-gray-700">
                {dayData.day}
              </div>
              <div className="flex-1 bg-gray-200 rounded-full h-8 relative">
                {dayData.totalNights > 0 && (
                  <div
                    className={`h-8 rounded-full ${getSuccessRateBarColor(dayData.successRate)} transition-all duration-500`}
                    style={{ width: `${dayData.successRate}%` }}
                  ></div>
                )}
                <div className="absolute inset-0 flex items-center justify-center text-sm font-medium text-gray-700">
                  {dayData.totalNights > 0 ? `${dayData.successRate}%` : 'No data'}
                </div>
              </div>
              <div className="w-16 text-sm text-gray-600 text-right">
                {dayData.totalNights > 0 ? `${dayData.goodNights}/${dayData.totalNights}` : '0/0'} nights
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Monthly Trends */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <SafeIcon icon={FiClock} className="text-blue-600 text-xl" />
          <h3 className="text-lg font-semibold text-gray-800">Monthly Trends</h3>
        </div>

        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-gray-800">{analyticsData.currentMonth}</h4>
            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getSuccessRateColor(analyticsData.monthSuccessRate)}`}>
              {analyticsData.monthSuccessRate}%
            </div>
          </div>
          <div className="flex-1 bg-gray-300 rounded-full h-3 mb-2">
            <div
              className={`h-3 rounded-full ${getSuccessRateBarColor(analyticsData.monthSuccessRate)} transition-all duration-500`}
              style={{ width: `${analyticsData.monthSuccessRate}%` }}
            ></div>
          </div>
          <p className="text-sm text-gray-600">
            {analyticsData.monthGoodNights}/{analyticsData.monthTotalNights} good nights
          </p>
        </div>
      </div>

      {/* Insights & Recommendations */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <SafeIcon icon={FiUser} className="text-purple-600 text-xl" />
          <h3 className="text-lg font-semibold text-gray-800">Insights & Recommendations</h3>
        </div>
        
        <div className="space-y-3 text-sm">
          {analyticsData.successRate >= 80 && (
            <div className="flex items-start space-x-2 text-green-700">
              <SafeIcon icon={FiTrendingUp} className="text-green-500 mt-0.5" />
              <p><strong>{child.name}</strong> is doing excellent with an {analyticsData.successRate}% success rate! Keep up the great bedtime routine.</p>
            </div>
          )}
          
          {analyticsData.successRate < 60 && (
            <div className="flex items-start space-x-2 text-orange-700">
              <SafeIcon icon={FiTarget} className="text-orange-500 mt-0.5" />
              <p>Consider reviewing <strong>{child.name}</strong>'s bedtime routine. A consistent schedule might help improve success rates.</p>
            </div>
          )}

          {analyticsData.bestDay.successRate > 0 && (
            <div className="flex items-start space-x-2 text-blue-700">
              <SafeIcon icon={FiCalendar} className="text-blue-500 mt-0.5" />
              <p><strong>{child.name}</strong> performs best on {analyticsData.bestDay.day}s with a {analyticsData.bestDay.successRate}% success rate. Consider what makes {analyticsData.bestDay.day}s special and try to replicate those conditions on other days.</p>
            </div>
          )}

          {stats.currentStreak >= 5 && (
            <div className="flex items-start space-x-2 text-purple-700">
              <SafeIcon icon={FiAward} className="text-purple-500 mt-0.5" />
              <p>Amazing! <strong>{child.name}</strong> is on a {stats.currentStreak}-night streak. This shows great consistency in the bedtime routine.</p>
            </div>
          )}

          {analyticsData.rewardsEarned > 0 && (
            <div className="flex items-start space-x-2 text-yellow-700">
              <SafeIcon icon={FiAward} className="text-yellow-500 mt-0.5" />
              <p><strong>{child.name}</strong> has earned {analyticsData.rewardsEarned} reward{analyticsData.rewardsEarned !== 1 ? 's' : ''} in this period. The reward system is working well as motivation!</p>
            </div>
          )}

          {analyticsData.recentSuccessRate > analyticsData.successRate && (
            <div className="flex items-start space-x-2 text-green-700">
              <SafeIcon icon={FiTrendingUp} className="text-green-500 mt-0.5" />
              <p>Great progress! <strong>{child.name}</strong>'s recent performance ({analyticsData.recentSuccessRate}%) is improving compared to the overall period average.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChildAnalytics;